<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Connect Aptly?</Text>
          <Text variant="body-large" color="secondary" align="center">
            Enter your Aptly feed URL to sync your property data
          </Text>
        </View>

        <View direction="column" gap="md">
          <FormField label="Feed URL" :error="form.fieldErrors('feedUrl')">
            <Input v-model="form.fields.feedUrl" placeholder="https://syndicates.getaptly.com/your-feed-id.json" />
          </FormField>
        </View>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" @click="connect" :loading="isConnecting" :disabled="form.validation.$invalid">
          Connect
        </Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { required, helpers } from '@vuelidate/validators';
import { ref } from 'vue';

import {
  Button,
  Modal,
  Text,
  View,
  WhatToExpectCard,
  WhatToExpectItem,
  useAlertsStore,
  FormField,
  Input,
  useForm,
} from '@tallo/design-system';
import { aptlyService, useCompanyStore } from '@tallo/investor/company';

const emit = defineEmits(['connected']);
const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();
const companyStore = useCompanyStore();
const isConnecting = ref(false);

const aptlyUrlValidator = helpers.withMessage(
  'Please enter a valid Aptly feed URL (format: https://syndicates.getaptly.com/[id].json)',
  (value: string) => {
    if (!value) return false;
    try {
      const url = new URL(value);
      return url.hostname === 'syndicates.getaptly.com' && url.pathname.endsWith('.json');
    } catch {
      return false;
    }
  },
);

const form = useForm<{
  feedUrl: string;
}>(
  {
    feedUrl: '',
  },
  {
    feedUrl: {
      required,
      aptlyUrl: aptlyUrlValidator,
    },
  },
);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'link-01',
    title: 'Sync property data',
    description: 'Tallo will automatically sync your property listings from Aptly.',
  },
  {
    icon: 'refresh',
    title: 'Keep data up to date',
    description: 'Property information will be updated automatically when changes are made in Aptly.',
  },
  {
    icon: 'sparkle',
    title: 'Streamlined workflow',
    description: 'Manage your properties seamlessly between Aptly and Tallo.',
  },
];

async function connect() {
  try {
    isConnecting.value = true;

    if (form.validation.$invalid) {
      return;
    }

    await aptlyService.connect(form.fields.feedUrl);
    await companyStore.refresh();

    alertsStore.success('Aptly connected successfully. Your properties are being synced.');
    emit('connected');
    close();
  } catch (error) {
    console.error('Failed to connect Aptly:', error);
    alertsStore.error('Failed to connect Aptly');
  } finally {
    isConnecting.value = false;
  }
}

function close() {
  modalRef.value?.close();
  form.fields.feedUrl = '';
  form.resetValidation();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
